import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Paper, Typography, Button, Chip, IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DashboardService } from '../../../services/dashboard.service';
import { ActionItem } from '../../../interfaces/dashboard.interface';
import { useAuth } from '../../../context/AuthContext';
import { PERMISSIONS } from '../../../constants/permissions';
import { getWidget, isWidgetVisible } from '../registry';
import { isGatedModule } from '../registry/moduleMeta';
import { useDashboardAccess } from '../useDashboardAccess';
import { PresetKey } from '../registry/presets';
import { loadPayrollActionItems } from './payrollActionItems';
import { RepairBillService } from '../../../services/maintenance.service';
import { buildWorkActions, isRolePreset } from './workSummary';
import { actionHelpKey } from './helpKeys';
import { helpElement } from '../../../help/attrs';
import { useActionCenterPrefs } from './useActionCenterPrefs';
import ActionCenterEditDialog from './ActionCenterEditDialog';
import { actionCardCatalog, actionCardKey } from './actionCenterCards';

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** Runs `call` only when allowed; a skipped or failed call resolves to null. */
const guarded = <T,>(allowed: boolean, call: () => Promise<T>): Promise<T | null> =>
    (allowed ? Promise.resolve().then(call).then(v => v ?? null).catch(() => null) : Promise.resolve(null));

/**
 * Decisions waiting outside leave, derived from the module summaries the caller can already see
 * (same gates as the matching widgets, so no new 403s). The server's action-center endpoint only
 * covers leave; these keep the board from saying "all caught up" while claims or requisitions wait.
 */
async function loadBusinessActions(institutionId: string, canSee: (widgetId: string) => boolean, canStore: boolean): Promise<ActionItem[]> {
    const [expense, queue, store, aging] = await Promise.allSettled([
        guarded(canSee('expense.summary'), () => DashboardService.getExpenseSummary(institutionId)),
        guarded(canSee('expense.queue'), () => DashboardService.getExpenseQueue(institutionId)),
        guarded(canStore, () => DashboardService.getStoreAdminDashboard(institutionId)),
        guarded(canSee('accounting.arAging'), () => DashboardService.getAccountingArAging(institutionId)),
    ]);
    const items: ActionItem[] = [];
    if (expense.status === 'fulfilled' && expense.value && expense.value.pendingCount > 0) {
        const rows = queue.status === 'fulfilled' && Array.isArray(queue.value) ? queue.value : [];
        const oldest = Math.max(0, ...rows.map(r => r.waitingDays ?? 0));
        items.push({
            id: 'expense.pending', severity: oldest > 14 ? 'CRITICAL' : 'WARNING', module: 'expense',
            title: 'Expense claims awaiting approval',
            detail: `${plural(expense.value.pendingCount, 'claim')} pending${oldest > 0 ? `, the oldest waiting ${plural(oldest, 'day')}` : ''}`,
            count: expense.value.pendingCount, actionLabel: 'Review claims', link: '/expense/all-claims',
        });
    }
    if (store.status === 'fulfilled' && store.value) {
        if (store.value.pendingApprovalRequisitions > 0) {
            items.push({
                id: 'requisition.pending', severity: 'WARNING', module: 'inventory',
                title: 'Store requisitions awaiting approval',
                detail: `${plural(store.value.pendingApprovalRequisitions, 'requisition')} waiting for a decision`,
                count: store.value.pendingApprovalRequisitions, actionLabel: 'Review requisitions', link: '/pending-approval-requisitions',
            });
        }
        if (store.value.lowStockItemCount > 0) {
            items.push({
                id: 'stock.low', severity: 'WARNING', module: 'inventory',
                title: 'Items below reorder level',
                detail: `${plural(store.value.lowStockItemCount, 'item')} running low`,
                count: store.value.lowStockItemCount, actionLabel: 'View low stock', link: '/low-stock',
            });
        }
    }
    if (aging.status === 'fulfilled' && Array.isArray(aging.value)) {
        const late = aging.value.filter(b => /^(61|90)/.test(b.bucket));
        const invoices = late.reduce((n, b) => n + b.invoiceCount, 0);
        if (invoices > 0) {
            items.push({
                id: 'ar.overdue', severity: 'WARNING', module: 'accounting',
                title: 'Receivables more than 60 days overdue',
                detail: `${plural(invoices, 'invoice')} still unpaid`,
                count: invoices, actionLabel: 'View invoices', link: '/sales/invoices',
            });
        }
    }
    return items;
}

/**
 * B-41 + DA-14: one payroll line per month in "Needs action", newest first, each with its state (awaiting approval,
 * or approved and unpaid with the legal deadline). Add-only: appended after every existing item, shown only to
 * holders of PAYROLL_APPROVE. The build lives in payrollActionItems.ts (shared with the Thread lens work items).
 */
const loadPayrollApprovalActions = (institutionId: string): Promise<ActionItem[]> => loadPayrollActionItems(institutionId);

/**
 * Maintenance repair bills waiting for accounts. Add-only, appended last, shown only to people who can create
 * supplier bills when accounting is on. One item with the count; the list page holds the detail.
 */
async function loadRepairBillActions(institutionId: string): Promise<ActionItem[]> {
    const waiting = await RepairBillService.waitingCount(institutionId);
    if (!(waiting > 0)) return [];
    return [{
        id: 'repairbill.waiting',
        severity: 'WARNING' as const,
        module: 'accounting',
        title: 'Repair bills waiting',
        detail: `${plural(waiting, 'repair bill')} from Maintenance waiting for approval`,
        count: waiting,
        actionLabel: 'Review bills',
        link: '/procurement/repair-bills',
    }];
}

export default function ActionCenter({ institutionId, preset }: { institutionId: string; preset?: PresetKey }) {
    const [items, setItems] = useState<ActionItem[] | null>(null);
    const navigate = useNavigate();

    const { hasPermission } = useAuth();
    const { enabledModules } = useDashboardAccess();
    const prefs = useActionCenterPrefs(institutionId);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        let live = true;
        const canSee = (widgetId: string) => {
            const def = getWidget(widgetId);
            return !!def && (!isGatedModule(def.module) || enabledModules.has(def.module)) && isWidgetVisible(def, hasPermission);
        };
        // The leave action center is guarded by DASHBOARD_VIEW only: callers without it (the Accountant) must not call it.
        const canLeaveActions = hasPermission(PERMISSIONS.DASHBOARD_VIEW);
        const canStore = enabledModules.has('inventory') && hasPermission(PERMISSIONS.DASHBOARD_INVENTORY_VIEW);
        const canApprovePayroll = hasPermission(PERMISSIONS.PAYROLL_APPROVE);
        const canApproveRepairBills = enabledModules.has('accounting') && hasPermission(PERMISSIONS.SUPPLIER_BILL_CREATE);
        if (isRolePreset(preset)) {
            // Role board: items come from the work summary (plus leave and claims for the manager).
            const manager = preset === 'manager';
            Promise.allSettled([
                manager ? guarded(canLeaveActions, () => DashboardService.getActionCenter(institutionId)) : Promise.resolve([] as ActionItem[]),
                manager ? loadBusinessActions(institutionId, canSee, canStore) : Promise.resolve([] as ActionItem[]),
                guarded(true, () => DashboardService.getWorkSummary(institutionId)),
                guarded(canApprovePayroll, () => loadPayrollApprovalActions(institutionId)),
                guarded(canApproveRepairBills, () => loadRepairBillActions(institutionId)),
            ]).then(([leave, business, ws, payroll, repairBills]) => {
                if (!live) return;
                const keep = (i: ActionItem) => !manager || !['stock.low', 'ar.overdue'].includes(i.id);
                const all = [
                    ...(ws.status === 'fulfilled' ? buildWorkActions(preset, ws.value) : []),
                    ...(leave.status === 'fulfilled' && Array.isArray(leave.value) ? leave.value : []),
                    ...(business.status === 'fulfilled' ? business.value : []),
                ].filter(keep);
                const payrollItems = payroll.status === 'fulfilled' && payroll.value ? payroll.value : [];
                const repairBillItems = repairBills.status === 'fulfilled' && repairBills.value ? repairBills.value : [];
                setItems([...all.filter(i => i.severity === 'CRITICAL'), ...all.filter(i => i.severity !== 'CRITICAL'), ...payrollItems, ...repairBillItems]);
            });
            return () => { live = false; };
        }
        Promise.allSettled([
            guarded(canLeaveActions, () => DashboardService.getActionCenter(institutionId)),
            loadBusinessActions(institutionId, canSee, canStore),
            guarded(canApprovePayroll, () => loadPayrollApprovalActions(institutionId)),
            guarded(canApproveRepairBills, () => loadRepairBillActions(institutionId)),
        ]).then(([leave, business, payroll, repairBills]) => {
            if (!live) return;
            const all = [
                ...(leave.status === 'fulfilled' && Array.isArray(leave.value) ? leave.value : []),
                ...(business.status === 'fulfilled' ? business.value : []),
            ];
            const payrollItems = payroll.status === 'fulfilled' && payroll.value ? payroll.value : [];
            // Critical first, server order otherwise; payroll approvals are appended last.
            const repairBillItems = repairBills.status === 'fulfilled' && repairBills.value ? repairBills.value : [];
            setItems([...all.filter(i => i.severity === 'CRITICAL'), ...all.filter(i => i.severity !== 'CRITICAL'), ...payrollItems, ...repairBillItems]);
        });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, enabledModules, preset]);

    const canSeeWidget = (widgetId: string) => {
        const def = getWidget(widgetId);
        return !!def && (!isGatedModule(def.module) || enabledModules.has(def.module)) && isWidgetVisible(def, hasPermission);
    };
    const cards = actionCardCatalog(preset, {
        leave: hasPermission(PERMISSIONS.DASHBOARD_VIEW),
        expense: canSeeWidget('expense.summary'),
        store: enabledModules.has('inventory') && hasPermission(PERMISSIONS.DASHBOARD_INVENTORY_VIEW),
        receivables: canSeeWidget('accounting.arAging'),
        payroll: hasPermission(PERMISSIONS.PAYROLL_APPROVE),
        repairBills: enabledModules.has('accounting') && hasPermission(PERMISSIONS.SUPPLIER_BILL_CREATE),
    }, items ?? []);
    const loading = items === null || !prefs.ready;
    const visibleItems = loading ? [] : items.filter(i => !prefs.hidden.has(actionCardKey(i.id)));

    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-help-section="dashboard.actionCenter">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: '0.35em' }}>
                <Typography variant="subtitle1" fontWeight="bold">Action Center</Typography>
                <Tooltip title="Edit cards">
                    <span>
                        <IconButton size="small" aria-label="Edit cards" disabled={prefs.saving || !prefs.ready || items === null} onClick={() => setEditing(true)}
                            data-help="dashboard.actionCenter.edit">
                            <EditOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
            {loading && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
            {!loading && items.length > 0 && visibleItems.length === 0 && (
                <Typography variant="body2" color="text.secondary" data-testid="action-center-all-hidden">
                    All cards are hidden. Use Edit cards to bring them back.
                </Typography>
            )}
            {!loading && items.length === 0 && (
                <Typography variant="body2" color="text.secondary">You're all caught up — nothing needs a decision right now.</Typography>
            )}
            <ActionCenterEditDialog
                open={editing}
                saving={prefs.saving}
                cards={cards}
                hidden={prefs.hidden}
                onClose={() => setEditing(false)}
                onSave={prefs.save}
            />
            {/* Tiles sharing the row, not one full-width row per item (that left most of the line empty). */}
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(340px, 1fr))' } }}>
                {visibleItems.map(item => {
                    const critical = item.severity === 'CRITICAL';
                    const tone = critical ? 'error' : 'warning';
                    return (
                        <Box
                            key={item.id}
                            data-help-solo=""
                            {...helpElement(actionHelpKey(item.id))}
                            sx={{
                                display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, p: 1.5, borderRadius: 1,
                                bgcolor: 'action.hover', borderLeft: 4, borderColor: `${tone}.main`,
                            }}
                        >
                            <Box sx={{ minWidth: 44, textAlign: 'center' }}>
                                {item.count > 0
                                    ? <Typography variant="h4" fontWeight={700} color={`${tone}.main`} lineHeight={1.1}>{item.count}</Typography>
                                    : (critical ? <ErrorOutlineIcon color="error" fontSize="large" /> : <WarningAmberIcon color="warning" fontSize="large" />)}
                            </Box>
                            <Box sx={{ flex: '1 1 180px', minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600}>{item.title}</Typography>
                                <Typography variant="caption" color="text.secondary" component="div">{item.detail}</Typography>
                                <Chip size="small" label={item.severity} color={tone} variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: 10 }} />
                            </Box>
                            <Button size="small" variant="contained" endIcon={<ArrowForwardIcon />} onClick={() => navigate(item.link)}>
                                {item.actionLabel}
                            </Button>
                        </Box>
                    );
                })}
            </Box>
        </Paper>
    );
}
